import React from "react";
import {useEffect, useState} from "react";
import useDebounce from "../../hooks/useDebounce";
import useOnclickOutside from "react-cool-onclickoutside";
import styled from "styled-components/macro";
import {useTheme} from "styled-components";
import {NewClear} from "../../icon/Icon";
import {Body1, SubheadGray, SubheadRegular} from "../../components/Font";
import {LoaderEnum, MiniLoader} from "../Loader/MiniLoader";



export interface DataItem {
    id: number | string
    title: string,
    subtitle: string
    userEmail?:string
    jobTitle?: string
}

export interface InputProps {
    onChange: (value: string) => void
    minLength?: number
    maxLength?: number
    required?: boolean
    value: string | null
    clear: () => void
    placeholder?: string
    error?: string
    type?: string
    mask?: string
    maskPlaceholder?: string
    autoFocus?: boolean
    searchable?: boolean
    data?: DataItem[]
    onClick?: (item: DataItem) => void
    disabled?: boolean
    caretBlock?: boolean
    onBlur?: () => void
    isFetching?: boolean
    clearDisable?: boolean
    noDebounce?: boolean
    regExp?: RegExp
    job?:boolean
    width?:string
    regExpErrorText?: string
    widthFlashingInput?: boolean
    shiftLeft?:string
}

export const InputWithSearchBox = ({
                                       disabled,
                                       onClick,
                                       data,
                                       onChange,
                                       minLength,
                                       maxLength,
                                       required,
                                       value,
                                       clear,
                                       placeholder,
                                       error,
                                       type,
                                       mask,
                                       autoFocus,
                                       onBlur,
                                       isFetching,
                                       clearDisable,
                                       noDebounce,
                                       regExp,
                                       width,
                                       regExpErrorText,
                                       shiftLeft,
                                       widthFlashingInput,
                                   }: InputProps): React.ReactElement => {
    const [inputValue, setInputValue] = useState<string>('')
    const [dataVisible, setDataVisible] = useState(false)
    const Theme = useTheme()

    const debouncedSearchTerm = useDebounce(inputValue || '', 300);
    const ref = useOnclickOutside(() => {
        setDataVisible(false)
    });

    useEffect(() => {
        if (debouncedSearchTerm !== null && value !== inputValue && !noDebounce) {
            onChange(debouncedSearchTerm)
            inputValue && setDataVisible(true)
        }
    }, [debouncedSearchTerm])

    useEffect(() => {
        if (noDebounce) {
            onChange(inputValue)
        }
    }, [inputValue])

    useEffect(() => {
        if (value !== null && value !== inputValue) setInputValue(value)
    }, [value])

    const [contentWidth, setContentWidth] = useState(0)

    return <InputSearchContainer style={{width: width || '100%'}}>
        {!mask && <InputString
            disabled={disabled}
            type={type || "text"}
            error={error}
            regError={!!(inputValue && regExp && !regExp.test(inputValue))}
            onBlur={onBlur}
            minLength={minLength || 0}
            maxLength={maxLength || 999}
            required={required || false}
            autoFocus={autoFocus || false}
            placeholder={placeholder || ''}
            value={inputValue || ''}
            onFocus={(e) => {
                setDataVisible(true)
                if (!e.currentTarget.className.includes('selectHeader')) {
                    e.currentTarget.classList.add('selectHeader')
                }
                setContentWidth(e.currentTarget.offsetWidth)
            }}
            onChange={(e) => {
                setInputValue(e.currentTarget.value)
                if (inputValue.length > 0) {
                    if (!e.currentTarget.className.includes('selectHeader')) {
                        e.currentTarget.classList.add('selectHeader')
                    }
                    setContentWidth(e.currentTarget.offsetWidth)
                }
            }}/>}


        {inputValue.length > 0 && (!disabled || clearDisable) && <InputIconContainer
            onClick={() => {
                clear()
                setInputValue('')
            }}>
            <NewClear />
        </InputIconContainer>}

        {dataVisible && (value && value?.length>1) && data && <InputSearchDataContainer>
            <InputSearchDataList ref={ref} style={{
                width: widthFlashingInput ? `100%`: `${contentWidth.toString()}px`,
                top: `8px`,
                left: shiftLeft ? shiftLeft:`0`,
            }}>
                {data.map((item, index) => {
                    return <InputSearchDataItem onClick={() => {
                        onClick && onClick(item)
                        setDataVisible(false)
                    }} key={index}>
                        <Body1>{item.title}</Body1>
                        <SubheadGray>{item.subtitle}</SubheadGray>
                    </InputSearchDataItem>
                })}
            </InputSearchDataList>
        </InputSearchDataContainer>}

        {isFetching && <InputSearchDataContainer>
            <InputSearchDataList ref={ref} style={{
                width: widthFlashingInput ? `100%`: `${contentWidth.toString()}px`,
                top: `8px`,
                left: shiftLeft ? shiftLeft:`0`,
                height: '256px',
                paddingTop:'100px'
            }}><MiniLoader color={Theme.color.indicator} size={LoaderEnum.medium}/>
            </InputSearchDataList>
        </InputSearchDataContainer>}

        {!isFetching
            && value
            && (value.length > 1)
            && data?.length === 0
            && dataVisible
            && <InputSearchDataContainer>
                <InputSearchDataList ref={ref} style={{
                    width: widthFlashingInput ? `100%`: `${contentWidth.toString()}px`,
                    top: `8px`,
                    left: shiftLeft ? shiftLeft:`0`,
                    height: '256px',
                }}><SubheadRegular>
                    Ничего не найдено
                </SubheadRegular>
                </InputSearchDataList>
            </InputSearchDataContainer>}

        <InputStringError error={error}>{error}</InputStringError>
        <InputStringError
            error={inputValue && regExp && !regExp.test(inputValue) ? regExpErrorText: ''}>{regExpErrorText}</InputStringError>
    </InputSearchContainer>
}

interface InputPropss {
    error?: string
    regError?: boolean
}

export const InputSearchContainer = styled.div`
  width: 100%;
  padding: 0;
  position: relative;
`

export const InputString = styled.input<InputPropss>`
  width: 100%;
  max-height: 56px;
  border: ${props => props.error ? '1px solid rgba(255, 5, 0, 1)' : '1px solid rgba(0, 0, 0, 0.08)'};
  border: ${props => props.regError ? '1px solid rgba(255, 5, 0, 1)' : '1px solid rgba(0, 0, 0, 0.08)'};
  box-sizing: border-box;
  border-radius: 4px;
  padding: 12px 12px 12px 12px;
  font-size: 14px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  :focus, :active, :focus-within {
    outline: none;
    border: ${props => props.regError ? '1px solid rgba(255, 5, 0, 1)' : '1px solid rgba(0, 122, 255, 1)'};
  }
`

export const InputIconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;
export const InputSearchDataList = styled.div`
  //width: 100%;
  align-items: center;
  background: #FFF;
  width:20px;

  padding: 0;
  position: absolute;
  top: 45px;
  display: flex;

  flex-direction: column;
  max-height: 256px;
  overflow-y: scroll;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(105, 105, 105, 0.25), 0 4px 10px rgba(105, 105, 105, 0.15);
  border-radius: 4px;

  ${SubheadRegular} {
    padding: 100px 0;
    text-align: center;
  }
`

export const InputSearchDataContainer = styled.div`
  position: fixed;
  margin: 0 auto;
  z-index: 1000;
  overflow: visible;
  width: 700px;
`

export const InputSearchDataItem = styled.div`
  width: 90%;
  cursor: pointer;
  padding: 12px;
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: start;
`

export const InputStringError = styled.div<InputPropss>`
  display: ${props => props.error ? 'flex' : 'none'};
  position: absolute;
  bottom: -17px;
  color: rgba(255, 5, 0, 1);
  font-weight: 300;
  font-size: 11px;
  left: 2px;
`